import React, { Suspense } from "react";

// ─── Pulse keyframes (shared across all skeletons) ─────────────────────────

const PULSE_STYLE = `
  @keyframes alpha-spin { to { transform: rotate(360deg); } }
  @keyframes alpha-pulse {
    0%, 100% { opacity: 0.45; }
    50%       { opacity: 0.9; }
  }
`;

// ─── Primitive skeleton block ───────────────────────────────────────────────

const Bone: React.FC<{
  w?: number | string;
  h?: number;
  radius?: number;
  delay?: number;
  style?: React.CSSProperties;
}> = ({ w = "100%", h = 12, radius = 4, delay = 0, style }) => (
  <div
    style={{
      width: w,
      height: h,
      borderRadius: radius,
      background: "var(--bg-elev)",
      animation: `alpha-pulse 1.5s ease-in-out ${delay}s infinite`,
      flexShrink: 0,
      ...style,
    }}
  />
);

// ─── Spinner (kept for inline / button use) ────────────────────────────────

/**
 * Token-driven indeterminate spinner.
 * Exported so other components can use the same branded spinner.
 */
export const Spinner: React.FC<{ size?: number }> = ({ size = 40 }) => {
  const border = Math.max(2, Math.round(size / 12));
  return (
    <>
      <style>{PULSE_STYLE}</style>
      <span
        role="status"
        aria-label="Loading"
        style={{
          display: "inline-block",
          width: size,
          height: size,
          border: `${border}px solid var(--bg-elev)`,
          borderTopColor: "var(--accent)",
          borderRadius: "50%",
          animation: "alpha-spin 0.7s linear infinite",
        }}
      />
    </>
  );
};

// ─── Page-level skeleton fallback ──────────────────────────────────────────

/**
 * PageLoadingFallback — skeleton shimmer shown while a lazy route chunk loads.
 * Replaces the old centered spinner + "Loading page..." text.
 */
export const PageLoadingFallback: React.FC<{ message?: string }> = () => (
  <div
    style={{
      padding: "24px 28px 40px",
      maxWidth: 1200,
      width: "100%",
    }}
    aria-busy="true"
    aria-label="Loading"
  >
    <style>{PULSE_STYLE}</style>

    {/* Eyebrow + title */}
    <Bone w={72} h={10} delay={0} style={{ marginBottom: 10 }} />
    <Bone w={220} h={22} radius={5} delay={0.05} style={{ marginBottom: 28 }} />

    {/* Toolbar-like strip */}
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 24,
      }}
    >
      <Bone w={200} h={32} radius={6} delay={0.08} />
      <Bone w={80} h={32} radius={6} delay={0.1} />
    </div>

    {/* Generic content rows — neutral skeleton that works for any page layout
        (lists, tables, detail panes). Tile grids were too specific and looked
        wrong on the majority of pages. */}
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {([88, 72, 95, 65, 80, 90] as const).map((w, i) => (
        <Bone key={i} w={`${w}%`} h={14} radius={4} delay={0.06 * i} />
      ))}
    </div>
  </div>
);

// ─── Component-level wrapper ───────────────────────────────────────────────

interface LazyLoadWrapperProps {
  children: React.ReactNode;
  fallbackMessage?: string;
}

/**
 * LazyLoadWrapper - Provides a consistent loading experience for lazy-loaded
 * components used with React.lazy() for code-splitting.
 */
export const LazyLoadWrapper: React.FC<LazyLoadWrapperProps> = ({
  children,
}) => {
  return <Suspense fallback={<PageLoadingFallback />}>{children}</Suspense>;
};

export default LazyLoadWrapper;
